<template>
  <div class="land-page">
    <v-container fluid class="land-container">
      <div class="breadcrumb-line">
        {{ $t("reports.reportsBreadcrumb") }} <span>›</span>
        {{ $t("reports.landSuitability") }}
      </div>

      <div class="page-heading">
        <div class="title-wrap">
          <h1>{{ $t("reports.landSuitability") }}</h1>
          <!-- <button class="guide-button" type="button">
            <v-icon size="15" color="white">mdi-help-circle-outline</v-icon>
            {{ $t("reports.aiReportGuide") }}
          </button> -->
        </div>
        <div class="heading-actions">
          <v-btn outlined color="primary" class="small-action" @click="goBack">
            {{ $t("reports.back") }}
          </v-btn>
          <v-btn
            outlined
            color="error"
            class="delete-action"
            :loading="deleting"
            @click="deleteDialog = true"
          >
            {{ $t("reports.deleteReport") }}
          </v-btn>
          <v-btn
            color="primary"
            class="primary-action"
            :loading="downloading"
            :disabled="!details"
            @click="downloadReport"
          >
            {{ $t("reports.downloadReport") }}
          </v-btn>
        </div>
      </div>

      <detail-report
        v-if="details"
        :details="details"
        :farm="details.userFarm || null"
        :report-id="$route.params.id"
        :image-layers="reportTiles"
        :active-image-index="activeTileIndex"
        @previous-image="stepImage(-1)"
        @next-image="stepImage(1)"
        @select-image="selectImage"
      >
        <template v-slot:map>
          <GmapMap
            :key="`ls-detail-map-${details.id}`"
            ref="detailMap"
            :center="mapCenter"
            :zoom="mapZoom"
            map-type-id="satellite"
            :options="mapOptions"
            class="detail-map"
            @tilesloaded="fitDetailMap"
          >
            <GmapMarker
              v-if="hasCoordinates && polygonPath.length < 3"
              :position="mapCenter"
            />
            <GmapPolygon
              v-if="polygonPath.length >= 3"
              :path="polygonPath"
              :options="polygonOptions"
            />
          </GmapMap>
        </template>
      </detail-report>
    </v-container>

    <confirm-box
      :dialog="deleteDialog"
      :title="$t('reports.deleteReport')"
      :message="$t('reports.confirmDeleteLandSuitabilityReport')"
      :confirm-btn-text="$t('delete')"
      :cancel-btn-text="$t('cancel')"
      :cancel-btn-outlined="true"
      @cancel:action="deleteDialog = false"
      @continue:action="deleteReport"
    />
  </div>
</template>

<script>
import DetailReport from "./components/LandSuitabilityDetailReport.vue";
import ConfirmBox from "@/components/ConfirmBox.vue";
import DownloadMixin from "@/mixins/DownloadMixin";
import loadingMixin from "@/mixins/LoadingMixin";
import LandSuitabilityAndWeatherService from "@/_services/LandSuitabilityAndWeatherService";

export default {
  name: "LandSuitabilityViewReport",
  components: {
    DetailReport,
    ConfirmBox,
  },
  mixins: [DownloadMixin, loadingMixin],
  data() {
    return {
      details: null,
      coordinates: {
        lat: 14.1,
        lng: -87.2,
      },
      polygonPath: [],
      activeTileIndex: 0,
      tileBlobPromises: {},
      tileBlobUrls: [],
      rasterOverlay: null,
      rasterOverlayMode: null,
      rasterOverlaySignature: null,
      PolygonTileOverlayClass: null,
      detailMapFitted: false,
      downloading: false,
      deleting: false,
      deleteDialog: false,
      mapZoom: 15,
      mapOptions: {
        disableDefaultUI: true,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
      },
      polygonOptions: {
        strokeColor: "#f2c94c",
        strokeOpacity: 1,
        strokeWeight: 3,
        fillColor: "#f6dd72",
        fillOpacity: 0.04,
      },
    };
  },
  computed: {
    hasCoordinates() {
      return Number.isFinite(Number(this.coordinates.lat)) &&
        Number.isFinite(Number(this.coordinates.lng));
    },
    mapCenter() {
      return {
        lat: Number(this.coordinates.lat) || 14.1,
        lng: Number(this.coordinates.lng) || -87.2,
      };
    },
    reportTiles() {
      const bbox = (this.details && this.details.bbox_coordinates) || {};
      const files = Array.isArray(bbox.landSuitabilityFiles)
        ? bbox.landSuitabilityFiles
        : [];
      const cropId = this.details && String(this.details.crop_id || "");
      const labels = {
        overall_score_file: "Overall suitability",
        soil_ph_file: "Soil pH",
        land_cover_file: "Land cover",
        soil_salinity_file: "Soil salinity",
        soil_organic_carbon_file: "Soil organic carbon",
        slope_file: "Slope",
        mean_min_temperature_file: "Minimum temperature",
        soil_texture_file: "Soil texture",
        road_proximity_file: "Road proximity",
        mean_temperature_file: "Mean temperature",
        dry_season_length_file: "Dry season length",
        mean_annual_precipitation_file: "Annual precipitation",
        coarse_fragments_file: "Coarse fragments",
        soil_depth_file: "Soil depth",
      };
      const tiles = files
        .filter(
          (file) =>
            file &&
            file.visualization_images_template_url &&
            (!cropId || String(file.crop_id || "") === cropId)
        )
        .map((file) => ({
          key: file.key,
          uuid: file.uuid,
          label: labels[file.key] || file.key,
          url: file.visualization_images_template_url,
          minZoom: Number(file.visualization_images_min_zoom) || 0,
          maxZoom: Number(file.visualization_images_max_zoom) || 22,
        }))
        .sort((left, right) =>
          left.key === "overall_score_file"
            ? -1
            : right.key === "overall_score_file"
              ? 1
              : 0
        );
      if (tiles.length) return tiles;

      const normalized =
        (this.details && this.details.map) || bbox.visualization || null;
      return normalized && normalized.tileUrl
        ? [{
            key: normalized.key || "overall_score_file",
            uuid: normalized.uuid,
            label: "Overall suitability",
            url: normalized.tileUrl,
            minZoom: Number(normalized.minZoom) || 0,
            maxZoom: Number(normalized.maxZoom) || 22,
          }]
        : [];
    },
    overallScoreTile() {
      return this.reportTiles[this.activeTileIndex] || this.reportTiles[0] || null;
    },
  },
  async mounted() {
    await this.loadReport();
  },
  beforeDestroy() {
    this.clearRasterOverlay();
    this.tileBlobUrls.forEach((url) => URL.revokeObjectURL(url));
    this.tileBlobUrls = [];
    this.tileBlobPromises = {};
  },
  methods: {
    goBack() {
      this.$router.push({ name: "landSuitabilityDesignList" });
    },
    async loadReport() {
      this.startLoading();
      try {
        await this.loadDetail();
        if (this.details && this.$route.query.download === "1") {
          await this.downloadRequestedReport();
        }
      } finally {
        this.stopLoading();
      }
    },
    async loadDetail() {
      try {
        const response = await LandSuitabilityAndWeatherService.getLandSuitabilityDetail(
          this.$route.params.id
        );
        if (!response || !response.data) {
          throw new Error(this.$t("reports.unableToLoadLandSuitabilityReport"));
        }
        this.details = response.data;
        this.applyDetailGeometry(response.data);
      } catch (error) {
        this.details = null;
        this.$notify({
          title: this.$t("reports.unableToLoadLandSuitabilityReport"),
          type: "error",
        });
      }
    },
    applyDetailGeometry(data) {
      this.detailMapFitted = false;
      this.activeTileIndex = 0;
      const lat = Number(data.latitude);
      const lng = Number(data.longitude);
      this.coordinates = {
        lat: Number.isFinite(lat) ? lat : 14.1,
        lng: Number.isFinite(lng) ? lng : -87.2,
      };

      const bbox = data.bbox_coordinates || {};
      const stored = bbox.geofenceCoordinates || bbox.geofence_coordinates || [];
      this.polygonPath = (Array.isArray(stored) ? stored : [])
        .map((point) => ({
          lat: Number(point.lat ?? point.latitude),
          lng: Number(point.lng ?? point.log ?? point.longitude),
        }))
        .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));
      this.mapZoom = this.polygonPath.length >= 3 ? 16 : 17;
      this.$nextTick(() => this.fitDetailMap());
    },
    selectImage(index) {
      if (!this.reportTiles.length) return;
      const count = this.reportTiles.length;
      this.activeTileIndex = ((Number(index) % count) + count) % count;
      this.$nextTick(() => this.fitDetailMap());
    },
    stepImage(direction) {
      this.selectImage(this.activeTileIndex + direction);
    },
    loadTileBlob(tile, zoom, x, y) {
      if (!tile.uuid) return Promise.reject(new Error("Missing tile UUID"));
      const cacheKey = [tile.uuid, zoom, x, y].join("|");
      if (!this.tileBlobPromises[cacheKey]) {
        this.tileBlobPromises[cacheKey] =
          LandSuitabilityAndWeatherService.getLandSuitabilityTile(
            this.$route.params.id,
            tile.uuid,
            zoom,
            x,
            y
          ).then((response) => {
            const url = URL.createObjectURL(response.data);
            this.tileBlobUrls.push(url);
            return url;
          });
      }
      return this.tileBlobPromises[cacheKey];
    },
    buildPolygonTileOverlayClass(google) {
      const tileX = (lng, zoom) =>
        Math.floor(((lng + 180) / 360) * (2 ** zoom));
      const tileY = (lat, zoom) => {
        const radians = (Math.max(-85.0511, Math.min(85.0511, lat)) * Math.PI) / 180;
        return Math.floor(
          ((1 - Math.asinh(Math.tan(radians)) / Math.PI) / 2) * (2 ** zoom)
        );
      };
      const tileLng = (x, zoom) => (x / (2 ** zoom)) * 360 - 180;
      const tileLat = (y, zoom) =>
        (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / (2 ** zoom)))) * 180) /
        Math.PI;

      return class PolygonTileOverlay extends google.maps.OverlayView {
        constructor(tile, polygon, opacity) {
          super();
          this.tile_ = tile;
          this.polygon_ = polygon;
          this.opacity_ = opacity;
          this.div_ = null;
          this.renderSignature_ = null;
        }
        onAdd() {
          this.div_ = document.createElement("div");
          this.div_.style.position = "absolute";
          this.div_.style.pointerEvents = "none";
          this.getPanes().overlayLayer.appendChild(this.div_);
        }
        draw() {
          if (!this.div_ || this.polygon_.length < 3) return;
          const projection = this.getProjection();
          const mapZoom = Math.round(this.getMap().getZoom());
          const sourceZoom = Math.max(
            this.tile_.minZoom,
            Math.min(mapZoom, this.tile_.maxZoom)
          );
          const pixels = this.polygon_.map((point) =>
            projection.fromLatLngToDivPixel(
              new google.maps.LatLng(point.lat, point.lng)
            )
          );
          const minX = Math.min(...pixels.map((point) => point.x));
          const maxX = Math.max(...pixels.map((point) => point.x));
          const minY = Math.min(...pixels.map((point) => point.y));
          const maxY = Math.max(...pixels.map((point) => point.y));
          this.div_.style.left = minX + "px";
          this.div_.style.top = minY + "px";
          this.div_.style.width = Math.max(1, maxX - minX) + "px";
          this.div_.style.height = Math.max(1, maxY - minY) + "px";
          this.div_.style.clipPath =
            "polygon(" +
            pixels
              .map((point) => (point.x - minX) + "px " + (point.y - minY) + "px")
              .join(",") +
            ")";

          const lngs = this.polygon_.map((point) => point.lng);
          const lats = this.polygon_.map((point) => point.lat);
          const minTileX = tileX(Math.min(...lngs), sourceZoom);
          const maxTileX = tileX(Math.max(...lngs), sourceZoom);
          const minTileY = tileY(Math.max(...lats), sourceZoom);
          const maxTileY = tileY(Math.min(...lats), sourceZoom);
          const signature = [
            this.tile_.url,
            mapZoom,
            sourceZoom,
            minTileX,
            maxTileX,
            minTileY,
            maxTileY,
          ].join("|");
          if (signature === this.renderSignature_) return;
          this.renderSignature_ = signature;
          this.div_.replaceChildren();

          for (let x = minTileX; x <= maxTileX; x += 1) {
            for (let y = minTileY; y <= maxTileY; y += 1) {
              const northWest = projection.fromLatLngToDivPixel(
                new google.maps.LatLng(tileLat(y, sourceZoom), tileLng(x, sourceZoom))
              );
              const southEast = projection.fromLatLngToDivPixel(
                new google.maps.LatLng(
                  tileLat(y + 1, sourceZoom),
                  tileLng(x + 1, sourceZoom)
                )
              );
              const image = document.createElement("img");
              image.style.position = "absolute";
              image.style.left = northWest.x - minX + "px";
              image.style.top = northWest.y - minY + "px";
              image.style.width = southEast.x - northWest.x + "px";
              image.style.height = southEast.y - northWest.y + "px";
              image.style.opacity = String(this.opacity_);
              this.div_.appendChild(image);
              this.tile_.loadTile(sourceZoom, x, y)
                .then((url) => {
                  if (this.div_ && image.isConnected) image.src = url;
                })
                .catch(() => image.remove());
            }
          }
        }
        onRemove() {
          if (this.div_ && this.div_.parentNode) {
            this.div_.parentNode.removeChild(this.div_);
          }
          this.div_ = null;
        }
      };
    },
    clearRasterOverlay(map = null) {
      const targetMap = map || (this.$refs.detailMap && this.$refs.detailMap.$mapObject);
      if (this.rasterOverlayMode === "polygon" && this.rasterOverlay) {
        this.rasterOverlay.setMap(null);
      } else if (targetMap && this.rasterOverlay) {
        const overlays = targetMap.overlayMapTypes;
        const index = overlays.getArray().indexOf(this.rasterOverlay);
        if (index >= 0) overlays.removeAt(index);
      }
      this.rasterOverlay = null;
      this.rasterOverlayMode = null;
      this.rasterOverlaySignature = null;
    },
    syncRasterOverlay(map) {
      const tile = this.overallScoreTile;
      if (!map || !window.google || !window.google.maps || !tile) {
        this.clearRasterOverlay(map);
        return;
      }
      const polygonSignature = this.polygonPath
        .map((point) => point.lat + "," + point.lng)
        .join(";");
      const signature = [tile.url, tile.minZoom, tile.maxZoom, polygonSignature].join("|");
      if (signature === this.rasterOverlaySignature) return;

      this.clearRasterOverlay(map);
      if (this.polygonPath.length >= 3) {
        if (!this.PolygonTileOverlayClass) {
          this.PolygonTileOverlayClass = this.buildPolygonTileOverlayClass(
            window.google
          );
        }
        this.rasterOverlay = new this.PolygonTileOverlayClass(
          {
            ...tile,
            loadTile: (zoom, x, y) => this.loadTileBlob(tile, zoom, x, y),
          },
          this.polygonPath,
          0.9
        );
        this.rasterOverlayMode = "polygon";
        this.rasterOverlay.setMap(map);
      } else {
        this.rasterOverlay = new window.google.maps.ImageMapType({
          getTileUrl: (coord, zoom) =>
            tile.url
              .replace("{z}", String(zoom))
              .replace("{x}", String(coord.x))
              .replace("{y}", String(coord.y)),
          tileSize: new window.google.maps.Size(256, 256),
          minZoom: tile.minZoom,
          maxZoom: tile.maxZoom,
          opacity: 0.82,
          name: "Land suitability score",
        });
        this.rasterOverlayMode = "map";
        map.overlayMapTypes.push(this.rasterOverlay);
      }
      this.rasterOverlaySignature = signature;
    },
    async fitDetailMap() {
      const mapRef = this.$refs.detailMap;
      if (!mapRef || !mapRef.$mapPromise) return;
      try {
        const map = await mapRef.$mapPromise;
        if (!map || !window.google || !window.google.maps) return;
        this.syncRasterOverlay(map);
        if (this.detailMapFitted) return;
        this.detailMapFitted = true;

        if (this.polygonPath.length >= 3) {
          const bounds = new window.google.maps.LatLngBounds();
          this.polygonPath.forEach((point) => bounds.extend(point));
          map.fitBounds(bounds, 48);
          return;
        }
        if (this.overallScoreTile && this.hasCoordinates) {
          map.setCenter(this.mapCenter);
          map.setZoom(this.overallScoreTile.minZoom);
          return;
        }
        if (this.hasCoordinates) {
          map.setCenter(this.mapCenter);
          map.setZoom(this.mapZoom);
        }
      } catch (error) {
        // The tilesloaded event retries once the map is ready.
      }
    },
    async downloadReport() {
      if (!this.details) return;
      this.downloading = true;
      try {
        const response = await LandSuitabilityAndWeatherService.downloadReport(
          { reportId: this.$route.params.id },
          "land_suitability"
        );
        this.downloadBlob(response.data, "land-suitability-report.pdf");
      } catch (error) {
        this.$notify({
          title: this.$t("reports.unableToDownloadReport"),
          type: "error",
        });
      } finally {
        this.downloading = false;
      }
    },
    async downloadRequestedReport() {
      await this.downloadReport();
      if (this.$route.query.download === "1") {
        await this.$router.replace({
          name: "landSuitabilityDesignView",
          params: { id: this.$route.params.id },
        });
      }
    },
    async deleteReport() {
      this.deleteDialog = false;
      this.deleting = true;
      try {
        const response =
          await LandSuitabilityAndWeatherService.deleteLandSuitabilityReport(
            this.$route.params.id
          );
        if (!response || response.success === false) {
          throw new Error(
            (response && (response.message || response.msg)) ||
              this.$t("reports.unableToDeleteReport")
          );
        }
        this.$notify({
          title: this.$t("reports.landSuitabilityReportDeletedSuccessfully"),
          type: "success",
        });
        await this.$router.replace({ name: "landSuitabilityDesignList" });
      } catch (error) {
        this.$notify({
          title:
            (error && error.message) ||
            this.$t("reports.unableToDeleteReportRetry"),
          type: "error",
        });
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.land-page {
  min-height: 100%;
  background: #f5f7fa;
  color: #20262d;
}

.land-container {
  padding: 28px 32px 42px;
}

.breadcrumb-line {
  margin-bottom: 12px;
  color: #7c858d;
  font-size: 12px;

  span {
    margin: 0 4px;
  }
}

.page-heading,
.title-wrap,
.heading-actions {
  display: flex;
  align-items: center;
}

.page-heading {
  justify-content: space-between;
  margin-bottom: 20px;
}

.title-wrap {
  gap: 14px;

  h1 {
    margin: 0;
    font-size: 27px;
    line-height: 1.2;
  }
}

.guide-button {
  display: inline-flex;
  min-height: 30px;
  align-items: center;
  gap: 7px;
  padding: 0 13px;
  border-radius: 16px;
  background: #18b879;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}

.heading-actions {
  gap: 12px;

  .v-btn {
    height: 40px;
    border-radius: 3px;
    box-shadow: none;
    font-size: 12px;
    font-weight: 700;
  }

  .small-action {
    min-width: 100px;
  }

  .primary-action,
  .delete-action {
    min-width: 150px;
  }
}

.detail-map {
  width: 100%;
  height: 480px;
}

@media (max-width: 1000px) {
  .page-heading {
    align-items: flex-start;
    gap: 16px;
    flex-direction: column;
  }
}
</style>
